import type { ActiveMembership } from '@/lib/types/auth.types';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/common/PageHeader';
import { UserChip } from '@/components/common/UserChip';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { ENTITY_LABEL } from '@/lib/constants/entity-types';
import { FULL_SCREEN_STATE_MIN_H } from '@/lib/constants/layout';
import { displayNameFromEmail } from '@/lib/utils/user.utils';
import { ProfileComingSoon } from './ProfileComingSoon';
import { profileRegistry } from './registry';
import { useEntityProfile } from './hooks/useEntityProfile';
import { EntityAccessBlockedState, blockedVariantForError } from './components/EntityAccessBlockedState';

/**
 * Renders the active entity's profile by resolving its module from the registry
 * (ADR FE-1). Type-agnostic: it never branches on `entity.type` or role. The
 * same component backs every persona profile route and the admin edit screen
 * (pass the target `entity` + `readOnly`).
 *
 * Since MYN-217 a ready module renders a full-bleed profile header above its
 * card grid, so it lays itself out through the shared `ProfileLayout` (the one
 * owner of the profile content column). This component only provides the
 * column for its own states: loading, error and "coming soon".
 */
export function ProfileEditor({ entity }: { entity: ActiveMembership }) {
  const mod = profileRegistry[entity.entityType];
  const isReady = mod.status === 'ready';
  const Sections = mod.Sections;
  const query = useEntityProfile(entity, { enabled: isReady });
  // Field-level editability goes through the can()/usePermission seam
  // (AUTHZ-1), never a raw canEdit comparison - this used to read
  // `entity.canEdit` directly, bypassing the seam.
  const canEditProfile = usePermission(PERMISSIONS.ENTITY_PROFILE_EDIT);
  const { user } = useAuth();
  const displayName = user ? displayNameFromEmail(user.email) : '';
  const blockedVariant = query.isError ? blockedVariantForError(query.error) : undefined;

  // Whether this route is blocked is only knowable once the fetch settles, so
  // commit to NO page chrome until then: rendering `PageHeader` while pending
  // made a "Startup Profile" title bar appear for the whole request and then
  // get yanked away when it 403'd, which reads worse than either end state
  // (MYN-131). Bare, centered in the same region the notice/content will
  // occupy, so there is no layout jump either way.
  if (isReady && query.isPending) {
    return (
      <div className={`flex ${FULL_SCREEN_STATE_MIN_H} items-center justify-center`}>
        <LoadingSpinner size="lg" label="Loading profile" />
      </div>
    );
  }

  // A suspended/deleted startup takes over the whole screen instead of
  // rendering as a card under the page chrome: a "Startup Profile" title bar
  // above it would announce the very profile the screen exists to say is
  // unavailable. Mirrors the work-email gate (`WorkEmailRequiredState`), which
  // likewise replaces the route's content before any PageHeader mounts.
  // Centering is `LinkStatusCard`'s `fullScreen`, which sizes against the
  // viewport rather than this route's ancestors - do NOT switch it back to a
  // percentage height and do not try to fix it with a wrapper here:
  // `AppLayout` gives `<main className="flex-1">` no definite height, so any
  // percentage height inside it collapses to content height and the card sits
  // high with a large gap beneath it. The page background already comes from
  // `AppLayout` (same `--color-page-bg`).
  if (isReady && blockedVariant) {
    return <EntityAccessBlockedState variant={blockedVariant} />;
  }

  // Ready modules open with their own profile header (MYN-217, FR-164), so the
  // old title strip only remains for a type whose profile is still coming soon.
  return (
    <div className="min-h-full bg-page-bg">
      {!isReady && (
        <PageHeader
          title={`${ENTITY_LABEL[entity.entityType]} Profile`}
          rightSlot={displayName ? <UserChip name={displayName} /> : undefined}
        />
      )}
      {/* A ready module's header runs full-bleed, so it owns its own content column. */}
      {isReady && !query.isError ? (
        <Sections data={query.data} readOnly={!canEditProfile} entityId={entity.entityId} />
      ) : (
        <div className="mx-auto flex w-full max-w-7xl flex-col px-4 py-8 md:px-6">
          {isReady ? <ErrorState onRetry={() => void query.refetch()} /> : <ProfileComingSoon />}
        </div>
      )}
    </div>
  );
}
